import { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Table, Stack, Container, Row, Col } from "react-bootstrap";
import { useMediaQuery } from 'react-responsive';

import supabase from "../utils/Supabase";
import { showToast } from "../utils/Toasts";
import { numberFormatter } from "../utils/Numbers";
import { getScripts } from "../utils/GetScripts";
import { applyFilter } from "../utils/ApplyFilter";
import { getTagFolders, getDateRangesFromView } from "../utils/Views";
import { FilterContext } from "../utils/Contexts";
import { useDebounce } from "../utils/useDebounce";
import { downloadFile, checkDownloadLimit } from "../utils/Database";
import { getUserInfo } from "../utils/UserUtil";
import { sortScripts } from '../utils/SortScripts';
import LS from "../utils/LS";
import { isPatreonLinkingAllowed } from "../utils/Patreon";
import { getDefaultBenefits, userHasBenefit } from "../utils/Benefits";

import useScriptsStore from "../stores/Scripts";
import useUserStore from "../stores/User";

import { saveFavorite, removeFavorite, getAllUserFavorites } from "../utils/Favorites";

import { LoadingRing } from "../components/LoadingRing";
import { LoadingText } from "../components/LoadingText";
import { TypewriterEffect } from "../components/TypewriterEffect";
import SortableHeader from "../components/SortableHeader";
import { ResponsiveCollapse } from "../components/ResponsiveCollapse";
import PageList from "../components/PageList";
import { ConfirmationModal,  } from "../components/Confirmation";

import {
  SearchText,
  SearchTagFolder,
  SearchPilotFilter,
  SearchDateSlider,
  SearchCheckbox,
} from "../components/SearchFields";

const DATABASE_VIEW_PREFS_KEY = "databaseViewPrefs";
const DATABASE_VIEW_PREFS_TTL = 24 * 60 * 60;

type SortConfig = { key: string; direction: "asc" | "desc" };
type DatabaseViewPrefs = {
  filters: Record<string, unknown>;
  sortConfig?: SortConfig;
};

function serializeFiltersForStorage( filters: Record<string, unknown> ) {
  const serialized: Record<string, unknown> = {};

  for ( const [key, value] of Object.entries( filters ) ) {
    if ( value === undefined || value === null || value === "" ) {
      continue;
    }

    if ( key === "Favorites" ) {
      if ( value ) {
        serialized.Favorites = true;
      }
      continue;
    }

    serialized[key] = value;
  }

  return serialized;
}

function readDatabaseViewPrefs(): DatabaseViewPrefs | null {
  const prefs = LS.fetch( DATABASE_VIEW_PREFS_KEY );

  if ( !prefs || typeof prefs !== "object" ) {
    return null;
  }

  const parsed = prefs as DatabaseViewPrefs;
  return {
    filters: parsed.filters && typeof parsed.filters === "object" ? parsed.filters : {},
    sortConfig: parsed.sortConfig,
  };
}

function buildInitialFilters( searchParams: URLSearchParams, storedFilters: Record<string, unknown> ) {
  const urlFilters: Record<string, unknown> = {};
  searchParams.forEach( ( value, key ) => {
    urlFilters[key] = value;
  } );

  if ( Object.keys( urlFilters ).length > 0 ) {
    return { ...storedFilters, ...urlFilters };
  }

  return { ...storedFilters };
}

function saveDatabaseViewPrefs( filters: Record<string, unknown>, sortConfig: SortConfig ) {
  LS.store(
    DATABASE_VIEW_PREFS_KEY,
    {
      filters: serializeFiltersForStorage( filters ),
      sortConfig,
    },
    DATABASE_VIEW_PREFS_TTL
  );
}

const ITEMS_PER_PAGE = 1000;

const patreonConfigured = !!process.env.REACT_APP_PATREON_CLIENT_ID;

function DatabasePage() {
  const navigate = useNavigate();
  const isSmall = useMediaQuery({ query: '(max-width: 576px)' });

  const [haveResults, setHaveResults] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [initialParamsApplied, setInitialParamsApplied] = useState(false);

  const totalScripts = useScriptsStore((state) => state.totalScripts);
  const setTotalScripts = useScriptsStore((state) => state.setTotalScripts);

  const [shownScripts, setShownScripts] = useState([]);

  const [tagFolders, setTagFolders] = useState<{ id: number; name: string; tags: string[] }[]>([]);
  const [hasTagFolders, setHasTagFolders] = useState(false);

  const [dateRanges, setDateRanges] = useState({});
  const [hasDateRanges, setHasDateRanges] = useState(false);

  const userData = useUserStore((state) => state.data);
  const setUserData = useUserStore((state) => state.setUserData);
  const [userId, setUserId] = useState(userData?.user_id || "");

  const [filteredScriptCount, setfilteredScriptCount] = useState(0);
  const [downloadingScriptId, setDownloadingScriptId] = useState<string | null>(null);
  const [unlimitedDownloads, setUnlimitedDownloads] = useState(userData?.isApprover || false);

  const totalScriptsCount = useScriptsStore((state) => state.totalCount);
  const setTotalScriptsCount = useScriptsStore((state) => state.setTotalCount);

  const [currentPage, setCurrentPage] = useState(1);

  const [canFavorite, setCanFavorite] = useState(userHasBenefit(userData, "scriptFavoriting"));

  const [favorites, setFavorites] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const storedPrefsRef = useRef( readDatabaseViewPrefs() );
  const skipNextPrefsWrite = useRef( true );

  // Initialize filters from URL params + localStorage (URL wins for shared links)
  const [search, setSearch] = useState( () => {
    const initialSearch = {};
    searchParams.forEach((value, key) => {
      initialSearch[key] = value;
    });
    return initialSearch;
  });

  const [filters, setFilters] = useState( () => {
    return buildInitialFilters( searchParams, storedPrefsRef.current?.filters || {} );
  } );

  const debouncedFilters = useDebounce(filters, 200); // Prevent typing lag in the UI
  const debouncedFiltersForURL = useDebounce(filters, 1000); // Longer debounce for URL updates

  const sortConfig = useScriptsStore((state) => state.sortConfig);
  const setSortConfig = useScriptsStore((state) => state.setSortConfig);
  const sortedScripts = useMemo(() => sortScripts(totalScripts, sortConfig), [totalScripts, sortConfig]);

  useEffect( () => {
    const storedSort = storedPrefsRef.current?.sortConfig;
    if (
      storedSort &&
      typeof storedSort.key === "string" &&
      ( storedSort.direction === "asc" || storedSort.direction === "desc" )
    ) {
      setSortConfig( storedSort );
    }
  }, [setSortConfig] );

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [okButton, setOkButton] = useState("OK");
  const [cancelButton, setCancelButton] = useState("Cancel");
  const [pendingAction, setPendingAction] = useState<((match?: any) => Promise<void>) | null>(null);
  const [hasSeenModal, setHasSeenModal] = useState(localStorage.getItem('seenPatreonModal') === 'true');

  useEffect(() => {
    if (userData?.patreon_linked) {
      localStorage.setItem('seenPatreonModal', 'true');
      setHasSeenModal(true);
      return;
    }

    // One-time prompt to link Patreon (respects the temp email allowlist)
    if (patreonConfigured && initialLoadComplete && !hasSeenModal && isPatreonLinkingAllowed( userData?.email )) {
      setModalTitle("Link Your Patreon Account!");
      setModalMessage("Patreon members can now link their accounts to automatically unlock exclusive features!\n\nGo to the Account page now to start the process and make sure your benefits stay current.");
      setOkButton("Link Account");
      setCancelButton("Maybe Later");

      setPendingAction(() => async () => {
        setShowModal(false);
        setModalMessage("");
        setModalTitle(null);
        setOkButton("OK");
        setCancelButton("Cancel");

        navigate('/account', { state: { from: "database" } });
      });

      const timer = setTimeout(() => {
          setShowModal(true);
          setHasSeenModal(true);
          localStorage.setItem('seenPatreonModal', 'true');
      }, 3000); // delay in ms

      return () => clearTimeout(timer);
    }
  }, [initialLoadComplete, userData, hasSeenModal]);

  useEffect(() => {
    setUnlimitedDownloads(userData?.isApprover || false);
  }, [userData]);

  useEffect(() => {
    // Don't filter until we've applied initial URL params
    if (!initialParamsApplied && totalScripts.length > 0) {
      return;
    }

    const results = applyFilter(sortedScripts, debouncedFilters);
    setfilteredScriptCount(results.length);
    setShownScripts(
      results.length > ITEMS_PER_PAGE ?
        results.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
        : results
    );
    setCurrentPage(1); // Reset to first page on filter change

    // Set both initialLoadComplete and haveResults to true after filtering is done
    // This ensures we don't show results until filters (including URL params) are applied
    if (totalScripts.length > 0) {
      setInitialLoadComplete(true);
      setHaveResults(true);
    }

  }, [totalScripts, debouncedFilters, initialParamsApplied]);

  // Separate effect for URL updates with longer debounce
  useEffect(() => {
    // Only update URL after initial params have been applied
    if (initialLoadComplete && initialParamsApplied) {
      const filteredSearchParams = Object.fromEntries(
        Object.entries(debouncedFiltersForURL)
          // Only update the URL for string filters, arrays aren't currently supported 
          .filter(([key, value]) => value && value !== "" && value !== key && typeof value !== "object" && !key.includes("_includeEmpty"))
          .map(([key, value]) => [key, String(value)])
      );

      // Skip the call entirely if the parameters haven't actually changed.
      const currentParams = Object.fromEntries(searchParams.entries());
      const currentKeys = Object.keys(currentParams);
      const newKeys = Object.keys(filteredSearchParams);
      const isSame =
        currentKeys.length === newKeys.length &&
        newKeys.every((key) => currentParams[key] === filteredSearchParams[key]);

      if (isSame) {
        return;
      }

      // Either update the URL or log a warning if it fails, but don't throw an error that would break the app. 
      try {
        setSearchParams(filteredSearchParams, { replace: true });
      } catch (error) {
        console.warn("Unable to sync search params to URL:", error);
      }
    }
  }, [debouncedFiltersForURL, initialLoadComplete, initialParamsApplied, setSearchParams]);

  // Persist filters + sort to localStorage (24h idle TTL)
  useEffect( () => {
    if ( !initialLoadComplete || !initialParamsApplied ) {
      return;
    }

    // Skip the first write after restore so empty defaults cannot wipe stored prefs on first paint
    if ( skipNextPrefsWrite.current ) {
      skipNextPrefsWrite.current = false;
      return;
    }

    saveDatabaseViewPrefs( debouncedFiltersForURL as Record<string, unknown>, sortConfig );
  }, [debouncedFiltersForURL, sortConfig, initialLoadComplete, initialParamsApplied] );

  // Restore Favorites checkbox intent as the live ID list once favorites are loaded
  useEffect( () => {
    if ( !favorites ) {
      return;
    }

    setFilters( ( prev ) => {
      if ( prev.Favorites === true ) {
        return { ...prev, Favorites: favorites };
      }
      return prev;
    } );
  }, [favorites] );

  useEffect(() => {
    async function fetchTagFolders() {
      const folders = await getTagFolders();
      setTagFolders(folders);
      setHasTagFolders(true);
    }

    if (!hasTagFolders) {
      fetchTagFolders();
    }
  }, [hasTagFolders]);

  useEffect(() => {
    async function fetchDateRanges() {
      const ranges = await getDateRangesFromView();
      if (ranges) {
        setDateRanges(ranges);
        setHasDateRanges(true);
      } else {
        showToast("error", "Unable to load date ranges for filtering.");
      }
    }

    if (!hasDateRanges) {
      fetchDateRanges();
    }
  }, [hasDateRanges]);

  useEffect(() => {
    // Only fetch scripts once
    if (totalScripts.length === 0) {
      getScripts().then((data) => {
        if (data) {
          setTotalScripts(data as []);
          setTotalScriptsCount(data.length);
        }
      });
    } else if (!initialParamsApplied) {
      // Mark that we've loaded the initial data with params already applied
      setTotalScriptsCount(totalScripts.length);
      setInitialParamsApplied(true);
    }
  }, [totalScripts, initialParamsApplied]);

  useEffect(() => {
    const results = applyFilter(sortedScripts, filters);
    
    setShownScripts(
      results.length > ITEMS_PER_PAGE ?
        results.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
        : results
    );
  }, [currentPage, sortConfig]);

  useEffect(() => {
    async function loadUserDataAndFavorites() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const userInfo = await getUserInfo(user.id);
          if (userInfo) {
            setUserData({ ...userInfo });
            setUserId(userInfo.user_id);
            
            if (userHasBenefit(userInfo, "scriptFavoriting")) {
              setCanFavorite(true);
              const favoritesData = await getAllUserFavorites(userInfo.user_id);
              const favoriteIds = favoritesData.map((row) => row.file_id);
              setFavorites(favoriteIds);
            }
          }
        }
      } catch (error) {
        console.log("Error fetching user data:", error);
        showToast("error", "Unable to get user data.");
      }
    }

    // Only load if we don't have userData yet
    if (!userData) {
      loadUserDataAndFavorites();
    } else if (userHasBenefit(userData, "scriptFavoriting") && !favorites) {
      // If userData already exists but favorites aren't loaded
      setCanFavorite(true);
      getAllUserFavorites(userId).then(data => {
        const favoriteIds = data.map((row) => row.file_id);
        setFavorites(favoriteIds);
      });
    }
  }, []); // Run only once on mount

  return (
    !hasTagFolders && !haveResults ? (<LoadingRing />) :
    (<>
      <div className="hive-container d-flex">
        <main>
          {/* Pass filters and setFilters to all child components with UseContext */}
          <FilterContext value={{ filters: filters, setFilters: setFilters, search: search }}>
            <div className="px-3 px-md-5">
                {isSmall && (<SearchText fieldName="Script Title" className="flex-grow-1" />)}
                <ResponsiveCollapse
                  title={"Advanced Search"}
                  isBreakPointActive={isSmall}
                >
                  <Stack direction={isSmall ? ("vertical") : ("horizontal")} gap={{xs: 0, sm: 3}} className="d-flex">
                    {!isSmall && (<SearchText fieldName="Script Title" className="flex-grow-1" />)}
                    <SearchText fieldName="Writer(s)" className="flex-grow-1" />
                    <SearchText fieldName="additional_info_concat" placeholder="Additional Info" className="flex-grow-1" />
                    {userData?.isApprover && (<SearchText fieldName="Indexed By" className="flex-grow-1" />)}
                  </Stack>
                  <Stack direction={isSmall ? ("vertical") : ("horizontal")} gap={3} className="d-flex flex-wrap mt-3 mt-sm-0">
                    {tagFolders.map(folder => (
                      <SearchTagFolder key={folder.name} folderName={folder.name} tags={folder.tags} className="flex-grow-1" />
                    ))}
                    {typeof filters['Category'] === 'string' && (filters['Category'] as string).toLowerCase().includes('television') && (
                      <SearchPilotFilter className="ms-1" />
                    )}
                    {canFavorite && (<SearchCheckbox fieldName="Favorites" values={favorites} />)}
                    {!hasDateRanges && (
                      <button className="btn gray-button ms-auto" onClick={() => { setFilters({}); setSearch({}); }}>Clear Filters</button>
                    )}
                  </Stack>
                  {hasDateRanges && (<Stack direction={isSmall ? ("vertical") : ("horizontal")} gap={{ xs: 0, sm: 3 }} className="d-flex">
                    <SearchDateSlider
                    fieldName="Draft Date"
                    minDate={dateRanges["Draft Date"][0]}
                    maxDate={dateRanges["Draft Date"][1]}
                    className="flex-grow-1"
                    />
                    <SearchDateSlider
                      fieldName="Date Added"
                      minDate={dateRanges["Date Added"][0]}
                      maxDate={dateRanges["Date Added"][1]}
                      className="flex-grow-1"
                    />
                    <button className="btn gray-button ms-md-auto my-sm-auto mb-3" onClick={() => { setFilters({}); setSearch({}); }}>Clear Filters</button>
                  </Stack>)}
                </ResponsiveCollapse>
              </div>
          </FilterContext>

          <div className="mx-md-5">
            {haveResults && (
              <Container fluid>
                <Row>
                  <Col xs={12} xl={4} className="d-flex flex-grow-1 justify-content-center justify-content-xl-start">
                    <p className="mb-2 mx-4 mx-md-0">
                        <TypewriterEffect speed={50}>
                          {numberFormatter(shownScripts.length)} out of{" "}
                          {shownScripts.length > filteredScriptCount
                            ? numberFormatter(totalScriptsCount)
                            : numberFormatter(filteredScriptCount)}{" "}
                          files shown
                        </TypewriterEffect>
                    </p>
                  </Col>
                  <Col xs={12} xl={4} className="d-flex flex-grow-1 justify-content-center">
                    <PageList
                      totalCount={filters ? filteredScriptCount : totalScriptsCount}
                      itemsPerPage={ITEMS_PER_PAGE}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                    />
                  </Col>
                  <Col xs={12} xl={4} className="d-flex flex-grow-1 justify-content-center justify-content-xl-end">
                    <p className="mb-2 mx-4 mx-md-0">
                      Questions or takedown requests? <a href="mailto:support@scripthive.com">Email us</a>.
                    </p>
                  </Col>
                </Row>
              </Container>
            )}
          </div>

          
          {(haveResults && shownScripts.length > 0) ? (
            <div className="script-table table-container mt-3 mb-4 mx-3 mx-md-5">
              <div className="table-responsive">
                  <Table striped hover size="sm">
                  <thead>
                    <tr>
                      {canFavorite && (
                        <th style={{ width: 30 }} className="text-center">
                          <i className="bi bi-star" aria-label="Favorite" title="Favorite"></i>
                        </th>
                      )}
                      <th style={{ width: 30 }} className="text-center">
                        <i className="bi bi-file-earmark-arrow-down" aria-label="Download" title="Download"></i>
                      </th>
                      <SortableHeader
                        label="Script Title"
                        sortKey="Script Title"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        minWidth={400}
                      />
                      <SortableHeader
                        label="Writer(s)"
                        sortKey="Writer(s)"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        minWidth={300}
                      />
                      <SortableHeader
                        label="Draft Date"
                        sortKey="Draft Date"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        width={150}
                      />
                      <SortableHeader
                        label="Additional Info"
                        sortKey="additional_info_concat"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        minWidth={200}
                      />
                      <SortableHeader
                        label="Date Added"
                        sortKey="Date Added"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        width={115}
                      />

                      {userData?.isApprover && (
                        <>
                          <th style={{ width: 30 }} className="text-center">
                            <i className="bi bi-pencil-square" aria-label="Edit" title="Edit"></i>
                          </th>
                          <SortableHeader
                            label="Indexed By"
                            sortKey="Indexed By"
                            sortConfig={sortConfig}
                            onSort={handleSort}
                            minWidth={150}
                          />
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {shownScripts
                      .filter(script => script["Script Title"] && script["Script Title"].trim() !== "")
                      .map((script) => {
                      return (<tr key={script.file_id}>
                        {canFavorite ?
                          <td className="h6 text-center" key={"fav_" + script.file_id}>
                            <button
                              id={"star_" + script.file_id}
                              className="icon-only-button"
                              onClick={() => {
                                if (favorites && favorites.indexOf(script.file_id) === -1) { // push to db
                                  saveFavorite(script.file_id, userId).then((success) => {
                                    if(success) {
                                      const newFavorites = favorites.concat(script.file_id);
                                      setFavorites(newFavorites);
                                      showToast("success", `Added ${script["Script Title"]} to Favorites`, 1000);
                                    } else {
                                      showToast("error", `Unable to save ${script["Script Title"]} to Favorites. Please try again.`);
                                    }
                                  });
                                } else { //delete from db
                                  removeFavorite(script.file_id, userId).then((success) => {
                                    if(success) {
                                      const newFavorites = favorites.filter((_, index) => index !== favorites.indexOf(script.file_id));
                                      setFavorites(newFavorites);
                                      showToast("info", `Removed ${script["Script Title"]} from Favorites`, 1000);
                                    } else {
                                      showToast("error", `Unable to remove ${script["Script Title"]} from Favorites. Please try again.`);
                                    }
                                  });
                                }
                              }}
                            >
                              <i className={`bi bi-star-fill${favorites && favorites.indexOf(script.file_id) > -1 ? " selected" : ""}`} aria-label="Favorite" title="Favorite"></i>
                            </button>
                          </td>
                          : <></>}
                        <td className="h6 text-center">
                          <button
                            className="icon-only-button"
                            style={{
                              cursor:
                                downloadingScriptId === script.file_id
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                            disabled={downloadingScriptId === script.file_id}
                            onClick={async () => {
                              if (downloadingScriptId !== null) return; // Prevent multiple downloads at once
                              setDownloadingScriptId(script.file_id);

                              let remaining = 0;
                              let downloadLimit = 0;

                              if (!unlimitedDownloads) {
                                const allowance = await checkDownloadLimit(userId);
                                if (!allowance) {
                                  setDownloadingScriptId(null);
                                  return;
                                }
                                remaining = allowance.remaining;
                                downloadLimit = allowance.limit;
                                console.log("Downloads remaining:", remaining);
                              }

                              if (remaining > 0 || unlimitedDownloads) {
                                const downloadSuccess = await downloadFile(script.file_id, userData?.user_id);
                                setDownloadingScriptId(null);
                                if (downloadSuccess && !unlimitedDownloads) {
                                  showToast("info", `You have ${(remaining - 1) === 0 ? "no more" : (remaining - 1)} download(s) remaining today.${downloadLimit > (getDefaultBenefits().downloadsPerDay as number) ? '' : ' Gold Level Patreons have a higher limit!'}`);
                                }
                              } else {
                                showToast("info", `Sorry, but you've reached the daily download limit of ${downloadLimit} per day. Please try again tomorrow.${downloadLimit > (getDefaultBenefits().downloadsPerDay as number) ? '' : ' Gold Level Patreons have a higher limit!'}`);
                                setDownloadingScriptId(null);
                              }
                            }}
                          >
                            {downloadingScriptId === script.file_id ? (
                              <LoadingText text="" className="mb-0" />
                            ) : (
                              <i className="bi bi-file-earmark-arrow-down-fill" aria-label="Download" title="Download"></i>
                            )}
                          </button>
                        </td>
                        <td className="h6" key={"title_" + script.file_id}>
                          <a
                            href={`/database/script/${script.file_id}`}
                            onClick={e => {
                              e.preventDefault(); // Prevent default anchor navigation
                              navigate(`/database/script/${script.file_id}`, { state: { from: "database" } }); // Use SPA navigation
                            }}
                          >
                            {script["Script Title"]}
                          </a>
                        </td>

                        <td className="h6" key={"wr_" + script.file_id}>
                          {script["Writer(s)"]}
                        </td>
                        <td className="h6" key={"dd_" + script.file_id}>
                          {script["Draft Date"]}
                        </td>
                        <td className="h6" key={"ai_" + script.file_id}>
                          {script["additional_info_concat"]}
                        </td>
                        <td className="h6" key={"da_" + script.file_id}>
                          {script["Date Added"]}
                        </td>
                        {userData?.isApprover && (
                          <>
                            <td key={"edit_" + script.file_id} className="text-center">
                              <button
                                className="icon-only-button"
                                onClick={() => {
                                  navigate(`/database/script/${script.file_id}/edit`, { state: { from: "database" } });
                                }}
                              >
                                <i className="bi bi-pencil-square" aria-label="Edit" title="Edit"></i>
                              </button>
                            </td>
                            <td>
                              {script && script.indexing && script.indexing.userData && script.indexing.userData?.email ? script.indexing.userData?.email : ''}
                            </td>
                          </>
                        )}
                      </tr>)
                    })}
                  </tbody>
                </Table>
              </div>
            </div>
          ) : haveResults ? (
              <div className="mt-3 mb-4 mx-3 mx-md-5">No scripts found.</div>
            ) : (
              <LoadingRing />
            )
          }
        </main>
      </div>
      <ConfirmationModal
          show={showModal}
          title={modalTitle}
          message={modalMessage}
          onConfirm={pendingAction}
          onHide={() => {
              setShowModal(false);
              setPendingAction(null);
          }}
          okButton={okButton}
          cancelButton={cancelButton}
      />
    </>)
  );
}
export default DatabasePage;
